import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { WeatherControls, WeatherSettings } from '@/components/WeatherControls';
import { AudioManager } from '@/components/AudioManager';
import { LightningManager } from '@/components/LightningBolt';

const Index = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [lightningTrigger, setLightningTrigger] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [thunderCount, setThunderCount] = useState(0);
  const [lightningBoltCount, setLightningBoltCount] = useState(0);
  const [groundStrikeCount, setGroundStrikeCount] = useState(0);
  const [currentSettings, setCurrentSettings] = useState<WeatherSettings>({
    rainIntensity: 100,
    rainSpeed: 50,
    lightningFrequency: 7,
    lightningIntensity: 85,
    cloudSpeed: 50,
    fogDensity: 50,
    windStrength: 30,
    masterVolume: 70,
    rainVolume: 60,
    thunderVolume: 85,
    backgroundMusicVolume: 50,
  });

  const sceneRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    cloudParticles: THREE.Mesh[];
    rainParticles: THREE.Points[];
    flash: THREE.PointLight;
    rain: THREE.Points;
    rainGeo: THREE.BufferGeometry;
    animationId: number;
    settings: WeatherSettings;
    thunderFlash: THREE.PointLight;
    screenFlash: THREE.Mesh;
    lightningManager: LightningManager;
    electroFlash: THREE.PointLight;
    ambientBoost: THREE.AmbientLight;
  } | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      1,
      1000
    );
    camera.position.z = 1;
    camera.rotation.x = 1.16;
    camera.rotation.y = -0.12;
    camera.rotation.z = 0.27;

    // Enhanced lighting system
    const ambient = new THREE.AmbientLight(0x444444, 0.3);
    scene.add(ambient);

    // Ambient boost for lightning illumination
    const ambientBoost = new THREE.AmbientLight(0x88aaff, 0);
    scene.add(ambientBoost);

    const directionalLight = new THREE.DirectionalLight(0xffeedd, 0.5);
    directionalLight.position.set(0, 0, 1);
    scene.add(directionalLight);

    // Primary lightning flash (more powerful)
    const flash = new THREE.PointLight(0x88aaff, 0, 600, 1.5);
    flash.position.set(200, 300, 100);
    scene.add(flash);

    // Secondary thunder flash
    const thunderFlash = new THREE.PointLight(0x6688ff, 0, 500, 1.8);
    thunderFlash.position.set(-150, 250, 80);
    scene.add(thunderFlash);

    // Additional electro flash for extra intensity
    const electroFlash = new THREE.PointLight(0xaaccff, 0, 400, 2.0);
    electroFlash.position.set(0, 350, 50);
    scene.add(electroFlash);

    // Enhanced screen flash effect
    const screenFlashGeometry = new THREE.PlaneGeometry(2000, 2000);
    const screenFlashMaterial = new THREE.MeshBasicMaterial({
      color: 0x88aaff,
      transparent: true,
      opacity: 0,
    });
    const screenFlash = new THREE.Mesh(screenFlashGeometry, screenFlashMaterial);
    screenFlash.position.set(0, 0, 0.5);
    scene.add(screenFlash);

    // Initialize Enhanced Lightning Manager
    const lightningManager = new LightningManager(scene);

    // Renderer with enhanced settings
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    scene.fog = new THREE.FogExp2(0x11111f, 0.002);
    renderer.setClearColor(scene.fog.color);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);

    // Enhanced rain system
    const cloudParticles: THREE.Mesh[] = [];
    const rainParticles: THREE.Points[] = [];
    const rainCount = 15000;
    
    const positions: number[] = [];
    const sizes: number[] = [];
    const rainGeo = new THREE.BufferGeometry();

    for (let i = 0; i < rainCount; i++) {
      positions.push(Math.random() * 400 - 200);
      positions.push(Math.random() * 500 - 250);
      positions.push(Math.random() * 400 - 200);
      sizes.push(30);
    }

    rainGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    rainGeo.setAttribute('size', new THREE.BufferAttribute(new Float32Array(sizes), 1));

    const rainMaterial = new THREE.PointsMaterial({
      color: 0xaaaaaa,
      size: 0.1,
      transparent: true,
    });

    const rain = new THREE.Points(rainGeo, rainMaterial);
    scene.add(rain);

    // Load cloud texture and create clouds
    const loader = new THREE.TextureLoader();
    loader.load(
      'https://static.vecteezy.com/system/resources/previews/010/884/548/original/dense-fluffy-puffs-of-white-smoke-and-fog-on-transparent-background-abstract-smoke-clouds-movement-blurred-out-of-focus-smoking-blows-from-machine-dry-ice-fly-fluttering-in-air-effect-texture-png.png',
      (texture) => {
        const cloudGeo = new THREE.PlaneGeometry(500, 500);
        const cloudMaterial = new THREE.MeshLambertMaterial({
          map: texture,
          transparent: true,
        });

        for (let p = 0; p < 30; p++) {
          const cloud = new THREE.Mesh(cloudGeo, cloudMaterial);
          cloud.position.set(
            Math.random() * 800 - 400,
            500,
            Math.random() * 500 - 450
          );
          cloud.rotation.x = 1.16;
          cloud.rotation.y = -0.12;
          cloud.rotation.z = Math.random() * 360;
          cloud.material.opacity = 0.6;
          cloud.receiveShadow = true;
          cloudParticles.push(cloud);
          scene.add(cloud);
        }
      }
    );

    // Store references
    sceneRef.current = {
      scene,
      camera,
      renderer,
      cloudParticles,
      rainParticles,
      flash,
      rain,
      rainGeo,
      animationId: 0,
      settings: currentSettings,
      thunderFlash,
      screenFlash,
      lightningManager,
      electroFlash,
      ambientBoost,
    };

    // Enhanced animation loop
    const animate = () => {
      if (!sceneRef.current) return;

      const { 
        scene, 
        camera, 
        renderer, 
        cloudParticles, 
        flash, 
        rain, 
        rainGeo, 
        settings, 
        thunderFlash, 
        screenFlash, 
        lightningManager, 
        electroFlash, 
        ambientBoost 
      } = sceneRef.current;

      // Update lightning manager
      lightningManager.update();

      // Update ground strike count
      const newGroundStrikeCount = lightningManager.getTotalGroundStrikes();
      if (newGroundStrikeCount !== groundStrikeCount) {
        setGroundStrikeCount(newGroundStrikeCount);
      }

      // Animate clouds
      const cloudSpeedMultiplier = settings.cloudSpeed / 100;
      cloudParticles.forEach((p) => {
        p.rotation.z -= 0.002 * cloudSpeedMultiplier;
      });

      // Animate rain
      const rainSpeedMultiplier = settings.rainSpeed / 100;
      const rainIntensityMultiplier = settings.rainIntensity / 100;
      
      (rain.material as THREE.PointsMaterial).opacity = Math.min(1, rainIntensityMultiplier * 0.8);
      
      const sizeArray = rainGeo.attributes.size.array as Float32Array;
      for (let i = 0; i < sizeArray.length; i++) {
        sizeArray[i] += (0.3 + settings.windStrength / 100) * rainSpeedMultiplier;
      }
      rainGeo.attributes.size.needsUpdate = true;

      rain.position.z -= 0.222 * rainSpeedMultiplier;
      if (rain.position.z < -200) {
        rain.position.z = 0;
      }

      // Wind effect
      const windOffset = Math.sin(Date.now() * 0.001) * (settings.windStrength / 100) * 0.5;
      rain.position.x = windOffset * 10;

      // Enhanced lightning system with more dramatic effects
      const lightningThreshold = 0.94 - (settings.lightningFrequency / 100) * 0.18;
      const lightningIntensityMultiplier = settings.lightningIntensity / 100;
      
      if (Math.random() > lightningThreshold || flash.intensity > 0.1) {
        if (flash.intensity < 0.1) {
          // Enhanced flash positioning with more variety
          const flashRadius = 300 + Math.random() * 200;
          const flashAngle = Math.random() * Math.PI * 2;
          
          flash.position.set(
            Math.cos(flashAngle) * flashRadius,
            300 + Math.random() * 250,
            Math.sin(flashAngle) * flashRadius
          );
          
          thunderFlash.position.set(
            Math.random() * 600 - 300,
            250 + Math.random() * 200,
            Math.random() * 400 - 200
          );
          
          electroFlash.position.set(
            Math.random() * 400 - 200,
            350 + Math.random() * 100,
            Math.random() * 300 - 150
          );
          
          // Create enhanced visual lightning bolts with ground strikes
          const boltIntensity = Math.min(150, settings.lightningIntensity + Math.random() * 30);
          lightningManager.createLightningBolt(flash.position.clone(), boltIntensity);
          
          // Create additional bolts for dramatic effect
          if (Math.random() > 0.5) {
            lightningManager.createLightningBolt(thunderFlash.position.clone(), boltIntensity * 0.8);
          }
          
          if (Math.random() > 0.7) {
            lightningManager.createLightningBolt(electroFlash.position.clone(), boltIntensity * 0.9);
          }
          
          // Trigger lightning for audio
          setLightningTrigger(true);
          setThunderCount(prev => prev + 1);
          setLightningBoltCount(prev => prev + 1);
          setTimeout(() => setLightningTrigger(false), 150);
        }
        
        // Enhanced lightning intensity with multiple light sources
        const baseIntensity = (80 + Math.random() * 400) * lightningIntensityMultiplier;
        flash.intensity = baseIntensity;
        thunderFlash.intensity = baseIntensity * 0.8;
        electroFlash.intensity = baseIntensity * 0.6;
        
        // Ambient lighting boost during lightning
        ambientBoost.intensity = baseIntensity * 0.1;
        
        // Enhanced screen flash for very intense lightning
        if (lightningIntensityMultiplier > 0.7) {
          const flashOpacity = 0.15 * lightningIntensityMultiplier;
          (screenFlash.material as THREE.MeshBasicMaterial).opacity = flashOpacity;
          
          // Color variation for screen flash
          if (Math.random() > 0.8) {
            (screenFlash.material as THREE.MeshBasicMaterial).color.setHex(0xffffff);
          } else {
            (screenFlash.material as THREE.MeshBasicMaterial).color.setHex(0x88aaff);
          }
        }
      }

      // Enhanced fade out effects
      if (flash.intensity > 0.1) {
        flash.intensity *= 0.92;
      }
      if (thunderFlash.intensity > 0.1) {
        thunderFlash.intensity *= 0.90;
      }
      if (electroFlash.intensity > 0.1) {
        electroFlash.intensity *= 0.88;
      }
      if (ambientBoost.intensity > 0.01) {
        ambientBoost.intensity *= 0.85;
      }
      if ((screenFlash.material as THREE.MeshBasicMaterial).opacity > 0) {
        (screenFlash.material as THREE.MeshBasicMaterial).opacity *= 0.75;
      }

      // Update fog density
      if (scene.fog && scene.fog instanceof THREE.FogExp2) {
        scene.fog.density = 0.002 * (settings.fogDensity / 100);
      }

      renderer.render(scene, camera);
      sceneRef.current.animationId = requestAnimationFrame(animate);
    };

    animate();

    // Window resize handler
    const handleResize = () => {
      if (!sceneRef.current) return;
      
      const { camera, renderer } = sceneRef.current;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (sceneRef.current) {
        cancelAnimationFrame(sceneRef.current.animationId);
        sceneRef.current.lightningManager.dispose();
        sceneRef.current.renderer.dispose();
        if (mountRef.current && sceneRef.current.renderer.domElement) {
          mountRef.current.removeChild(sceneRef.current.renderer.domElement);
        }
      }
    };
  }, []);

  const handleSettingsChange = (newSettings: WeatherSettings) => {
    setCurrentSettings(newSettings);
    if (sceneRef.current) {
      sceneRef.current.settings = newSettings;
    }
  };

  const handleAudioReady = (ready: boolean) => {
    setAudioReady(ready);
  };

  return (
    <div className="w-full h-screen overflow-hidden bg-black">
      <div ref={mountRef} className="w-full h-full" />
      
      {/* Enhanced UI Overlay */}
      <div className="absolute top-4 left-4 text-white z-10">
        <h1 className="text-2xl font-bold mb-2">⚡ Enhanced Lightning Storm</h1>
        <p className="text-sm text-gray-300">
          Dramatic branching lightning with ground strike particle effects
        </p>
        {currentSettings.backgroundMusicVolume > 0 && (
          <p className="text-xs text-blue-300 mt-1">
            🎵 Background music: {audioReady ? 'Playing' : 'Loading...'}
          </p>
        )}
        <p className="text-xs text-yellow-300 mt-1">
          ⚡ Thunder strikes: {thunderCount}
        </p>
        <p className="text-xs text-purple-300 mt-1">
          🌩️ Lightning bolts: {lightningBoltCount}
        </p>
        <p className="text-xs text-orange-300 mt-1">
          🎆 Ground strikes: {groundStrikeCount}
        </p>
        <p className="text-xs text-cyan-300 mt-1">
          ✨ NEW: Ground impact sparks & debris
        </p>
      </div>
      
      {/* Enhanced Live Stats */}
      <div className="absolute bottom-4 left-4 text-white text-sm z-10">
        <div className="bg-black/60 p-3 rounded-lg backdrop-blur-sm border border-gray-700">
          <p className="mb-1">🌧️ Rain particles: 15,000</p>
          <p className="mb-1">☁️ Storm clouds: 30</p>
          <p className="mb-1">⚡ Lightning: Enhanced fractal bolts</p>
          <p className="mb-1">🌩️ Thunder: 3 realistic variations</p>
          <p className="mb-1">🔊 Audio: {audioReady ? 'Enabled' : 'Click to enable'}</p>
          <p className="mb-1">💡 Lighting: Multi-source illumination</p>
          <p className="mb-1">✨ Effects: Glow, pulse, and fade</p>
          <p className="mb-1">🎆 NEW: Ground strike particles</p>
          <p>💥 Sparks, debris & ground flashes</p>
        </div>
      </div>

      {/* Weather Controls */}
      <WeatherControls
        onSettingsChange={handleSettingsChange}
        isVisible={controlsVisible}
        onToggleVisibility={() => setControlsVisible(!controlsVisible)}
      />

      {/* Audio Manager */}
      <AudioManager
        settings={currentSettings}
        lightningTrigger={lightningTrigger}
        masterVolume={currentSettings.masterVolume}
        rainVolume={currentSettings.rainVolume}
        thunderVolume={currentSettings.thunderVolume}
        backgroundMusicVolume={currentSettings.backgroundMusicVolume}
        onAudioReady={handleAudioReady}
      />
    </div>
  );
};

export default Index;